import { nanoid } from "nanoid";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

// Helper function to generate example data from JSON schema
function generateExampleFromSchema(
  schema: unknown,
  fieldName?: string,
): unknown {
  if (!schema || typeof schema !== "object") {
    return {};
  }

  const schemaObj = schema as Record<string, unknown>;

  // Check for explicit example value first
  if (schemaObj.example !== undefined) {
    return schemaObj.example;
  }

  // Handle oneOf/anyOf by taking the first schema
  if (Array.isArray(schemaObj.oneOf) && schemaObj.oneOf.length > 0) {
    return generateExampleFromSchema(schemaObj.oneOf[0], fieldName);
  }
  if (Array.isArray(schemaObj.anyOf) && schemaObj.anyOf.length > 0) {
    return generateExampleFromSchema(schemaObj.anyOf[0], fieldName);
  }

  // Handle allOf by merging properties
  if (Array.isArray(schemaObj.allOf) && schemaObj.allOf.length > 0) {
    const merged: Record<string, unknown> = {};
    for (const subSchema of schemaObj.allOf) {
      if (subSchema && typeof subSchema === "object") {
        const subExample = generateExampleFromSchema(subSchema, fieldName);
        if (subExample && typeof subExample === "object") {
          Object.assign(merged, subExample);
        }
      }
    }
    return merged;
  }

  // Handle objects - either explicit type:"object" or implicit via properties field
  if (
    (schemaObj.type === "object" || schemaObj.properties) &&
    schemaObj.properties
  ) {
    const example: Record<string, unknown> = {};
    const properties = schemaObj.properties as Record<string, unknown>;

    for (const [key, prop] of Object.entries(properties)) {
      if (prop && typeof prop === "object") {
        const propObj = prop as Record<string, unknown>;
        example[key] = generateExampleFromSchema(propObj, key);
      }
    }
    return example;
  }

  if (schemaObj.type === "array" && schemaObj.items) {
    return [generateExampleFromSchema(schemaObj.items, fieldName)];
  }

  switch (schemaObj.type) {
    case "string":
      // Provide more meaningful default based on field name or format if available
      if (typeof schemaObj.format === "string") {
        switch (schemaObj.format) {
          case "email":
            return "user@example.com";
          case "date":
            return "2024-01-01";
          case "date-time":
            return "2024-01-01T12:00:00Z";
          case "uri":
          case "url":
            return "https://example.com";
          default:
            return generateStringExample(fieldName);
        }
      }
      return generateStringExample(fieldName);
    case "number":
    case "integer":
      return generateNumberExample(fieldName);
    case "boolean":
      return true;
    default:
      // If no type is specified but we have properties, treat as object
      if (schemaObj.properties) {
        const example: Record<string, unknown> = {};
        const properties = schemaObj.properties as Record<string, unknown>;

        for (const [key, prop] of Object.entries(properties)) {
          if (prop && typeof prop === "object") {
            const propObj = prop as Record<string, unknown>;
            example[key] = generateExampleFromSchema(propObj, key);
          }
        }
        return example;
      }
      // For completely unknown types, return empty object
      return {};
  }
}

// Helper to generate meaningful string examples based on field name
function generateStringExample(fieldName?: string): string {
  if (!fieldName) {
    return "example";
  }

  const lowerName = fieldName.toLowerCase();
  if (lowerName.includes("title")) {
    return "Sample Title";
  }
  if (lowerName.includes("name")) {
    return "Sample Name";
  }
  if (lowerName.includes("description")) {
    return "Sample description";
  }
  if (lowerName.includes("email")) {
    return "user@example.com";
  }
  if (lowerName.includes("id")) {
    return "sample-id";
  }
  if (lowerName.includes("url") || lowerName.includes("link")) {
    return "https://example.com";
  }

  return "example";
}

// Helper to generate meaningful number examples based on field name
function generateNumberExample(fieldName?: string): number {
  if (!fieldName) {
    return 42;
  }

  const lowerName = fieldName.toLowerCase();
  if (lowerName.includes("price") || lowerName.includes("cost")) {
    return 99.99;
  }
  if (lowerName.includes("age")) {
    return 25;
  }
  if (lowerName.includes("count") || lowerName.includes("quantity")) {
    return 1;
  }
  if (lowerName.includes("id")) {
    return 123;
  }

  return 42;
}

export interface RequestParam {
  id: string;
  enabled: boolean;
  name: string;
  value: string;
}

export interface RequestHeader {
  id: string;
  enabled: boolean;
  name: string;
  value: string;
}

interface RequestState {
  params: RequestParam[];
  headers: RequestHeader[];
  body: string;

  // Param actions
  updateParam: (id: string, updates: Partial<RequestParam>) => void;
  addParam: (param: Omit<RequestParam, "id">) => void;
  removeParam: (id: string) => void;

  // Header actions
  updateHeader: (id: string, updates: Partial<RequestHeader>) => void;
  addHeader: (header: Omit<RequestHeader, "id">) => void;
  removeHeader: (id: string) => void;

  // Body actions
  setBody: (body: string) => void;

  // Auto-populate from endpoint
  populateFromEndpoint: (endpoint: {
    pathParams: string[];
    queryParams: string[];
    requiredHeaders: string[];
    bodySchema: unknown;
    method: string;
  }) => void;

  // Reset
  resetRequest: () => void;
}

const initialParams: RequestParam[] = [];

const initialHeaders: RequestHeader[] = [];

const initialBody = "";

export const useRequestStore = create<RequestState>()(
  devtools(
    (set) => ({
      params: initialParams,
      headers: initialHeaders,
      body: initialBody,

      updateParam: (id, updates) =>
        set((state) => {
          // If updating the name, check for duplicates
          if (updates.name !== undefined) {
            const existingParam = state.params.find(
              (p) => p.id !== id && p.name === updates.name,
            );
            if (existingParam) {
              return state; // Don't update if duplicate name exists
            }
          }
          return {
            params: state.params.map((param) =>
              param.id === id ? { ...param, ...updates } : param,
            ),
          };
        }),

      addParam: (param) =>
        set((state) => ({
          params: [...state.params, { ...param, id: nanoid() }],
        })),

      removeParam: (id) =>
        set((state) => ({
          params: state.params.filter((param) => param.id !== id),
        })),

      updateHeader: (id, updates) =>
        set((state) => {
          // If updating the name, check for duplicates
          if (updates.name !== undefined) {
            const existingHeader = state.headers.find(
              (h) => h.id !== id && h.name === updates.name,
            );
            if (existingHeader) {
              return state; // Don't update if duplicate name exists
            }
          }
          return {
            headers: state.headers.map((header) =>
              header.id === id ? { ...header, ...updates } : header,
            ),
          };
        }),

      addHeader: (header) =>
        set((state) => ({
          headers: [...state.headers, { ...header, id: nanoid() }],
        })),

      removeHeader: (id) =>
        set((state) => ({
          headers: state.headers.filter((header) => header.id !== id),
        })),

      setBody: (body) => set({ body }),

      populateFromEndpoint: (endpoint) =>
        set(() => {
          const newParams: RequestParam[] = [];
          const newHeaders: RequestHeader[] = [];

          // Add path parameters
          for (const pathParam of endpoint.pathParams) {
            newParams.push({
              id: nanoid(),
              enabled: true,
              name: pathParam,
              value: `{${pathParam}}`, // Placeholder value
            });
          }

          // Add query parameters
          for (const queryParam of endpoint.queryParams) {
            newParams.push({
              id: nanoid(),
              enabled: true,
              name: queryParam,
              value: "", // Empty value for user to fill
            });
          }

          // Add required headers
          for (const headerName of endpoint.requiredHeaders) {
            let defaultValue = "";
            if (headerName.toLowerCase() === "content-type") {
              defaultValue = "application/json";
            }
            newHeaders.push({
              id: nanoid(),
              enabled: true,
              name: headerName,
              value: defaultValue,
            });
          }

          // Add standard headers for non-GET requests
          if (!["GET", "HEAD"].includes(endpoint.method.toUpperCase())) {
            // Only add Content-Type if not already in required headers
            if (
              !endpoint.requiredHeaders.some(
                (h) => h.toLowerCase() === "content-type",
              )
            ) {
              newHeaders.push({
                id: nanoid(),
                enabled: true,
                name: "Content-Type",
                value: "application/json",
              });
            }
          }

          // Generate example body from schema
          let newBody = "";
          if (
            endpoint.bodySchema &&
            !["GET", "HEAD"].includes(endpoint.method.toUpperCase())
          ) {
            console.log("Generating example from schema:", {
              method: endpoint.method,
              bodySchema: endpoint.bodySchema,
            });
            try {
              const exampleData = generateExampleFromSchema(
                endpoint.bodySchema,
                "requestBody",
              );
              console.log("Generated example data:", exampleData);
              newBody = JSON.stringify(exampleData, null, 2);
              console.log("Final body string:", newBody);
            } catch (error) {
              console.error("Error generating example from schema:", error);
              newBody = "{}";
            }
          }

          return {
            params: newParams,
            headers: newHeaders,
            body: newBody,
          };
        }),

      resetRequest: () =>
        set({
          params: initialParams,
          headers: initialHeaders,
          body: initialBody,
        }),
    }),
    {
      name: "request-store",
    },
  ),
);
